import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AgencyDashboard.css';
import { logout } from '../../services/authService';
import AgencyDashboardPage from './AgencyDashboardPage';
import AgencyVehicleManagement from './AgencyVehicleManagement';
import AgencyDriverManagement from './AgencyDriverManagement';
import AgencyTripManagement from './AgencyTripManagement';
import AgencyBillGeneration from './AgencyBillGeneration';
import AgencyCustomerManagement from './AgencyCustomerManagement';
import AgencyUserManagement from './AgencyUserManagement';
import AgencyBookingRequests from './AgencyBookingRequests';
import AgencyReportsAnalytics from './AgencyReportsAnalytics';
import AgencyDocumentManagement from './AgencyDocumentManagement';
import AgencySupportIssues from './AgencySupportIssues';

const AgencyDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  // Load activeMenu from localStorage, default to 'dashboard' if not set
  const [activeMenu, setActiveMenu] = useState(() => {
    const savedMenu = localStorage.getItem('agencyActiveMenu');
    return savedMenu || 'dashboard';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [agencyName, setAgencyName] = useState('Agency');
  const profileDropdownRef = useRef(null);
  const sidebarRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  // Save activeMenu to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('agencyActiveMenu', activeMenu);
  }, [activeMenu]);

  // Load agency name from localStorage
  useEffect(() => {
    const savedAgency = localStorage.getItem('agencyProfile');
    if (savedAgency) {
      try {
        const agency = JSON.parse(savedAgency);
        setAgencyName(agency.name || 'Agency');
      } catch (error) {
        console.error('Error loading agency profile:', error);
      }
    }
  }, []);

  const menuItems = {
    main: [
      { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
      { id: 'vehicle-management', label: 'Vehicle Management', icon: '🚚' },
      { id: 'driver-management', label: 'Driver Management', icon: '👤' },
      { id: 'trip-management', label: 'Trip Management', icon: '🛣️' },
      { id: 'bill-generation', label: 'Bill Generation', icon: '📄' },
    ],
    management: [
      { id: 'customer-management', label: 'Customer Management', icon: '👥' },
      { id: 'user-management', label: 'User Management', icon: '👤' },
      { id: 'booking-requests', label: 'Booking Request', icon: '📋' },
    ],
    system: [
      { id: 'reports-analytics', label: 'Reports and Analysis', icon: '📈' },
      { id: 'document-management', label: 'Document Management', icon: '📋' },
      { id: 'support-issues', label: 'Support / Issues', icon: '⚠️' },
    ],
  };

  const menuComponents = {
    'dashboard': AgencyDashboardPage,
    'vehicle-management': AgencyVehicleManagement,
    'driver-management': AgencyDriverManagement,
    'trip-management': AgencyTripManagement,
    'bill-generation': AgencyBillGeneration,
    'customer-management': AgencyCustomerManagement,
    'user-management': AgencyUserManagement,
    'booking-requests': AgencyBookingRequests,
    'reports-analytics': AgencyReportsAnalytics,
    'document-management': AgencyDocumentManagement,
    'support-issues': AgencySupportIssues,
  };

  // Dummy notifications data
  const notifications = [
    { id: 1, message: 'Vehicle TN58AB1234 insurance expiring in 3 days', time: '10 minutes ago', type: 'warning', icon: '⚠️' },
    { id: 2, message: 'Trip #4582 is delayed by 2 hours', time: '45 minutes ago', type: 'delay', icon: '🕐' },
    { id: 3, message: 'Trip #4580 has been completed successfully', time: '1 hour ago', type: 'success', icon: '✓' },
    { id: 4, message: 'New booking request received', time: '2 hours ago', type: 'info', icon: '📄' },
    { id: 5, message: 'Payment of ₹45,000 received from customer', time: '3 hours ago', type: 'payment', icon: '💳' },
    { id: 6, message: 'Driver license expiring soon for John Doe', time: '4 hours ago', type: 'warning', icon: '⚠️' },
  ];

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    // Close mobile menu when a menu item is clicked
    setMobileMenuOpen(false);
    // Also collapse the sidebar so it hides on desktop until user opens it again
    setSidebarCollapsed(true);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const getMenuLabel = (menuId) => {
    const allItems = [...menuItems.main, ...menuItems.management, ...menuItems.system];
    const item = allItems.find(item => item.id === menuId);
    return item ? item.label : 'Dashboard';
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const handleLogoutClick = () => {
    setProfileDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    // Clear saved menu when logging out
    localStorage.removeItem('agencyActiveMenu');
    // Use authService to clear all auth data
    logout();
    if (onLogout) {
      onLogout();
    }
    // Navigate to home page
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const toggleNotificationDropdown = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
    setProfileDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
      // Close mobile menu when clicking outside
      if (sidebarRef.current && mobileMenuOpen && !sidebarRef.current.contains(event.target)) {
        // Check if click is not on mobile menu button
        const mobileMenuBtn = document.querySelector('.agency-mobile-menu-btn');
        if (mobileMenuBtn && !mobileMenuBtn.contains(event.target)) {
          setMobileMenuOpen(false);
        }
      }
    };

    if (profileDropdownOpen || notificationDropdownOpen || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [profileDropdownOpen, notificationDropdownOpen, mobileMenuOpen]);

  return (
    <div className="agency-dashboard-container">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className={`agency-mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}
      
      {/* Top Header Bar */}
      <div className="agency-top-header">
        <div className="agency-header-left">
          <button className="agency-mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
            <span className={mobileMenuOpen ? 'agency-hamburger open' : 'agency-hamburger'}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <h1 className="agency-page-title">
            Nettaa Logistics Service
            {activeMenu !== 'dashboard' && (
              <span className="agency-page-subtitle"> - {getMenuLabel(activeMenu)}</span>
            )}
          </h1>
        </div>
        <div className="agency-header-right">
          <div className="agency-notifications-container" ref={notificationDropdownRef}>
            <button className="agency-notifications-btn" onClick={toggleNotificationDropdown} aria-label="Notifications">
              <span className="agency-notification-icon">🔔</span>
              <span className="agency-notification-badge">{notifications.length}</span>
            </button>
            {notificationDropdownOpen && (
              <div className="agency-notification-dropdown">
                <div className="agency-notification-dropdown-header">
                  <h3>Notifications</h3>
                  <button className="agency-mark-all-read-btn" onClick={() => setNotificationDropdownOpen(false)}>
                    Mark All Read
                  </button>
                </div>
                <div className="agency-notification-dropdown-list">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="agency-notification-dropdown-item">
                      <span className={`agency-notification-dropdown-icon ${notification.type}`}>
                        {notification.icon}
                      </span>
                      <div className="agency-notification-dropdown-content">
                        <div className="agency-notification-dropdown-text">{notification.message}</div>
                        <div className="agency-notification-dropdown-time">{notification.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="agency-notification-dropdown-footer">
                  <a href="#" className="agency-view-all-notifications-link">View All Notifications →</a>
                </div>
              </div>
            )}
          </div>
          <div className="agency-user-profile-container" ref={profileDropdownRef}>
            <div className="agency-user-profile" onClick={toggleProfileDropdown}>
              <div className="agency-user-avatar">A</div>
              <div className="agency-user-info">
                <span className="agency-user-name">{agencyName}</span>
                <span className="agency-user-role">Agency</span>
              </div>
              <button className={`agency-dropdown-arrow ${profileDropdownOpen ? 'open' : ''}`}>▼</button>
            </div>
            {profileDropdownOpen && (
              <div className="agency-profile-dropdown">
                <button className="agency-dropdown-item agency-logout-item" onClick={handleLogoutClick}>
                  <span className="agency-dropdown-icon">🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Left Sidebar */}
      <div 
        ref={sidebarRef}
        className={`agency-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="agency-sidebar-header">
          <div className="agency-logo">
            <div className="agency-logo-icon">🚚</div>
          </div>
          {!sidebarCollapsed && <h2 className="agency-logo-text">Nettaa</h2>}
          <button 
            className="agency-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        <nav className="agency-sidebar-nav">
          {/* MAIN Section */}
          <div className="agency-nav-section">
            {!sidebarCollapsed && <h3 className="agency-nav-section-title">MAIN</h3>}
            <ul className="agency-nav-list">
              {menuItems.main.map((item) => (
                <li key={item.id}>
                  <button
                    className={`agency-nav-item ${activeMenu === item.id ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="agency-nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="agency-nav-label">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* MANAGEMENT Section */}
          <div className="agency-nav-section">
            {!sidebarCollapsed && <h3 className="agency-nav-section-title">MANAGEMENT</h3>}
            <ul className="agency-nav-list">
              {menuItems.management.map((item) => (
                <li key={item.id}>
                  <button
                    className={`agency-nav-item ${activeMenu === item.id ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="agency-nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="agency-nav-label">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* SYSTEM Section */}
          <div className="agency-nav-section">
            {!sidebarCollapsed && <h3 className="agency-nav-section-title">SYSTEM</h3>}
            <ul className="agency-nav-list">
              {menuItems.system.map((item) => (
                <li key={item.id}>
                  <button
                    className={`agency-nav-item ${activeMenu === item.id ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="agency-nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="agency-nav-label">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* User Info at Bottom */}
        <div className="agency-sidebar-footer">
          <div className="agency-user-profile-sidebar">
            <div className="agency-user-avatar-sidebar">A</div>
            {!sidebarCollapsed && (
              <div className="agency-user-details-sidebar">
                <span className="agency-user-name-sidebar">{agencyName}</span>
                <span className="agency-user-role-sidebar">Agency</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`agency-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {(() => {
          const ActiveComponent = menuComponents[activeMenu];
          if (ActiveComponent) {
            return <ActiveComponent />;
          }
          return (
            <div className="agency-content-wrapper">
              <h1 className="agency-coming-soon-title">{getMenuLabel(activeMenu)}</h1>
              <p className="agency-coming-soon-message">is coming soon...</p>
            </div>
          );
        })()}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="agency-modal-overlay" onClick={handleLogoutCancel}>
          <div className="agency-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="agency-modal-header">
              <h3>Confirm Logout</h3>
            </div>
            <div className="agency-modal-body">
              <p>Are you sure you want to logout?</p>
            </div>
            <div className="agency-modal-footer">
              <button className="agency-modal-button agency-cancel-button" onClick={handleLogoutCancel}>
                No
              </button>
              <button className="agency-modal-button agency-confirm-button" onClick={handleLogoutConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyDashboard;

